import { useState, useCallback, useMemo } from 'react';
import download from 'downloadjs';
import qs from 'query-string';

import useFetchApi from 'hooks/useFetchApi';
import { Status, NonClientError } from 'hooks/requests/fetchUtils';
import catchOnly from 'utils/catchOnly';
import { reject, isNil, indexOf } from 'ramda';
import useQueryParameters from 'hooks/useQueryParameters';
import { useSelector } from 'react-redux';
import { selectAccounts, selectInstantCredit } from 'state/instantCredit';

type WalletHook = () => [
  (vendor: 'google' | 'apple', storeNumber?: string | null) => Promise<void>,
  Status,
  boolean,
];

const PATH = '/api/wallet';

export class WalletError extends Error {}

const useWalletDownload: WalletHook = () => {
  const [error, setError] = useState<boolean>(false);
  const [status, fetch] = useFetchApi();

  const { cardType: qpCardType, overrideKey } = useQueryParameters();
  const { logo, divisionNumber, cardType: icCardType } = useSelector(
    selectInstantCredit,
  );

  const accounts = useSelector(selectAccounts);
  const currentAccount = useSelector(selectInstantCredit);

  const accountIndex = useMemo(() => {
    const index = indexOf(currentAccount, accounts);
    return index !== -1 ? index : undefined;
  }, [currentAccount, accounts]);

  const downloadWallet: ReturnType<WalletHook>[0] = useCallback(
    async (vendor, storeNumber) => {
      let newTab;
      if (vendor === 'google') {
        /**
         * To avoid popup blocker, a new tab must be open first in the
         * event handler of a user interaction (in this case, onClick).
         * Doing that after making a fetch request is not considered to be
         * a user interaction in Firefox.
         */
        newTab = window.open('about:blank', '_blank');
      }
      try {
        setError(false);
        const query = reject(isNil, {
          cardType: icCardType || qpCardType,
          overrideKey,
          logo: logo,
          storeNumber,
          vendor,
          redirect: false,
          divisionNumber: divisionNumber,
          accountIndex,
        });

        const response = await fetch(`${PATH}?${qs.stringify(query)}`, {
          method: 'GET',
          redirect: 'manual',
        });

        if (
          (response.headers.get('Content-Type') || '').match(
            /application\/vnd.apple.pkpass/,
          )
        ) {
          download(
            await response.blob(),
            'wallet.pkpass',
            'application/vnd.apple.pkpass',
          );
        } else if (newTab) {
          const body = await response.json();
          const url = body.google_pay_url;
          newTab.location.href = url;
        }
      } catch (e) {
        if (catchOnly(e, NonClientError)) {
          setError(true);
          if (newTab) {
            newTab.close();
          }
          return Promise.reject(new WalletError());
        }
      }
    },
    [
      icCardType,
      qpCardType,
      overrideKey,
      logo,
      divisionNumber,
      accountIndex,
      fetch,
    ],
  );

  return [downloadWallet, status, error];
};

export default useWalletDownload;
